import { SERVICE_CALL_RESPONSE_CODES } from "../entities/PlpType";
import { FormSectionElements, FormSections, LOVValues } from "../entities/formTemplateDataType";
import { GroupItem, LineItem } from "../runtimeComponent/subComponent/order/models";
import { getFieldByName } from "../utilities/ElementUtils";
import NumericValueUtils from "../utilities/NumericValueUtils";
import { AbstractResponseHandler } from "./AbstractResponseHandler";

/**
 * To handle service-call responses in GLP.
 */
export class GlpResponseHandler implements AbstractResponseHandler {
  constructor() {}

  handle(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    const name = fieldOrSection instanceof FormSectionElements ? fieldOrSection.LoadBindName : fieldOrSection.ENUTitle;
    console.log('about to execute handle for ' + name + ' in PlpResponseHandler...');
    
    switch (name) {
      case 'Continue':
        return this.handleIdentificationLookup(response, fieldOrSection);
        break;
      case 'Review order':
        return this.handleReviewOrder(response, fieldOrSection);
        break;
      case 'Pay now':
        return this.handleCompletePayment(response, fieldOrSection);
        break;
      default:
        console.log('No handler for ' + name);
        break;
    }
  }

  private handleIdentificationLookup(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    const INVALID_DL_ENG = 'Driver\'s licence number is invalid. Please double check the number you have entered and try again.';
    const INVALID_DL_FR = 'Le numéro du permis de conduire n’est pas valide.';
    const LESS_CURRENT_DL_ENG = 'The Driver\'s Licence Number you entered is not the most current on file. Please enter your current number or <a href="https://www.ontario.ca/locations/serviceontario" style="color:#0066cc;">visit your nearest ServiceOntario</a> for support';
    const LESS_CURRENT_DL_FR = 'Le numéro de permis de conduire que vous avez saisi ne correspond pas au numéro actuel figurant au dossier. Veuillez saisir votre numéro actuel ou <a href="https://www.ontario.ca/locations/serviceontario" style="color:#0066cc;">vous rendre au centre ServiceOntario</a> le plus proche pour obtenir de l’aide.';

    const INVALID_RIN_ENG = 'Registrant identification number is invalid. Please double check the number you have entered and try again.';
    const INVALID_RIN_FR = 'Le numéro d’identification du titulaire n’est pas valide';
    const INVALID_RIN_TYPE_ENG = 'The RIN must be ‘I’ (Individual), ‘J’ (Joint) or ‘C’ (Company).”';
    const INVALID_RIN_TYPE_FR = 'Le NIT doit être « I » (Individu), « J » (Copropriété) ou « C » (Compagnie).';


    if ((response.statusCode === SERVICE_CALL_RESPONSE_CODES.RIN_DL_NOT_EXISTS) || (response === SERVICE_CALL_RESPONSE_CODES.RIN_DL_NOT_EXISTS)) {
      const identificationType = fieldOrSection.findFieldByName('identificationType');
      if (identificationType?.Value === 'Driver\'s licence number') {
        const dlInputElement = fieldOrSection.findFieldByName('driverLicenceNumber');
        if (dlInputElement) {
          dlInputElement.ErrorMessage = INVALID_DL_ENG;  // Reset to default invalid validation error msg.
          dlInputElement.ErrorMessageFRA = INVALID_DL_FR;
          dlInputElement.Touched = true;  // To show element-level error msg.
          dlInputElement.Valid = false;
        }
      } 
      else {
        const rinInputElement = fieldOrSection.findFieldByName('registrantInformationNumber');
        if (rinInputElement) {
          rinInputElement.ErrorMessage = INVALID_RIN_ENG;  // Reset to default invalid validation error msg.
          rinInputElement.ErrorMessageFRA = INVALID_RIN_FR;
          rinInputElement.Touched = true;  // To show element-level error msg.
          rinInputElement.Valid = false;
        }
      }
      return { processSuccessful: false};
    } else if ((response.statusCode === SERVICE_CALL_RESPONSE_CODES.DL_NOT_MOST_CURRENT) || (response === SERVICE_CALL_RESPONSE_CODES.DL_NOT_MOST_CURRENT)) {  // REG013 error.
      const identificationType = fieldOrSection.findFieldByName('identificationType');
      if (identificationType?.Value === 'Driver\'s licence number') {
        const dlInputElement = fieldOrSection.findFieldByName('driverLicenceNumber');
        if (dlInputElement) {
          dlInputElement.ErrorMessageRichText = LESS_CURRENT_DL_ENG;
          dlInputElement.ErrorMessageRichTextFRA = LESS_CURRENT_DL_FR;
          dlInputElement.Touched = true;  // To show element-level error msg.
          dlInputElement.Valid = false;
        }
      }
      return { processSuccessful: false};
    } else if ((response.statusCode === SERVICE_CALL_RESPONSE_CODES.RIN_INVALID_TYPE) || (response === SERVICE_CALL_RESPONSE_CODES.RIN_INVALID_TYPE)) {
      const identificationType = fieldOrSection.findFieldByName('identificationType');
      if (identificationType?.Value === 'Registrant identification number (RIN)') {
        const rinInputElement = fieldOrSection.findFieldByName('registrantInformationNumber');
        if (rinInputElement) {
          rinInputElement.ErrorMessage = INVALID_RIN_TYPE_ENG;  // Set to specific error msg.
          rinInputElement.ErrorMessageFRA = INVALID_RIN_TYPE_FR;
          rinInputElement.Touched = true;  // To show element-level error msg.
          rinInputElement.Valid = false;
        }
      }
      return { processSuccessful: false};
    }
    return { processSuccessful: true};
  }

  private handleReviewOrder(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    if ((response.statusCode == SERVICE_CALL_RESPONSE_CODES.PROCESS_SUCCESSFUL) || (response == SERVICE_CALL_RESPONSE_CODES.PROCESS_SUCCESSFUL)) {
      const orderItemField = getFieldByName('orderItemDetails', fieldOrSection);
      if (orderItemField) {
        orderItemField.Value = this.generateOrderBreakdown(response, orderItemField);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return { processSuccessful: true, stepIndex: 'Order summary' };  // Go to 'Order Summary' page.
      }
    }
    return { processSuccessful: false, stepIndex: 'Review order' };  // Stay on 'Review order' page.
  }

  private getGraphicTypeLov(field: FormSectionElements | FormSections, graphicType: string) {
    let graphicTypeField: FormSectionElements;
    if (field instanceof  FormSectionElements) {
      graphicTypeField = (field.parent as FormSections).findFieldByName('graphicType');
    } else {
      graphicTypeField = (field.parent.parent as FormSections).findFieldByName('graphicType');
    }
    return (graphicTypeField?.ListOfLOVValues?.length ? graphicTypeField.ListOfLOVValues.find((lov) => lov.code === graphicType) : null);
  }

  private getPlateCount(plateInfo: any) {
    if (plateInfo.plateCount) {
      return plateInfo.plateCount.toString();
    } else {
      return '1';
    }
  }

  private generateOrderBreakdown(response: any, field: FormSectionElements | FormSections): any {
    const orderBreakdown = {};

    const plateTypeMapEnglish = {
      PASSENGER: 'Passenger plate',
      COMMERCIAL: 'Commercial plate',
    };
    const plateTypeMapFrench = {
      PASSENGER: 'Plaque pour véhicule de tourisme',
      COMMERCIAL: 'Plaque pour véhicule utilitaire',
    };

    const sloganLanguageMapEnglish = {
      '0000': 'English',
      '0001': 'French',
    };
    const sloganLanguageMapFrench = {
      '0000': 'Anglaise',
      '0001': 'Français',
    };

    let isEng = true;
    const url = window.location.href;
    if (url.indexOf("/fr") > 0) {
        isEng = false;
    }

    const products: GroupItem[] = [];

    const itemEntriesEn = [];
    const itemEntriesFr = [];
    const subTotalEntriesEn = [];
    const subTotalEntriesFr = [];
    const totalEntriesEn = [];
    const totalEntriesFr = [];

    response.priceBreakdown.map((orderItem, index) => {
      const productDetailEntriesEn = [];
      const productDetailEntriesFr = [];
      const itemDetailEntriesEn = [];
      const itemDetailEntriesFr = [];
      const productLineItems: LineItem[] = [];
      const productAdditionalProperties = [];

      const graphicTypeLov = this.getGraphicTypeLov(field, orderItem.plateInfo.graphicType);
      if (graphicTypeLov) {
        productDetailEntriesEn.push({name: graphicTypeLov.description + ' graphic', value: NumericValueUtils.toAmountInEnglish(orderItem.totalItemCost.unitCost)});
        itemEntriesEn.push({name: graphicTypeLov.description + ' graphic', value: NumericValueUtils.toAmountInEnglish(orderItem.totalItemCost.unitCost)});
        productDetailEntriesFr.push({name: graphicTypeLov.FRADescription + ' graphique', value: NumericValueUtils.toAmountInFrench(orderItem.totalItemCost.unitCost)});
        itemEntriesFr.push({name: graphicTypeLov.FRADescription + ' graphique', value: NumericValueUtils.toAmountInFrench(orderItem.totalItemCost.unitCost)});
      }
      itemDetailEntriesEn.push({name: 'Type of plate', value: plateTypeMapEnglish[orderItem.plateInfo.plateType]});
      itemDetailEntriesFr.push({name: 'Type de plaque', value: plateTypeMapFrench[orderItem.plateInfo.plateType]});
      itemDetailEntriesEn.push({name: 'Slogan language', value: sloganLanguageMapEnglish[orderItem.plateInfo.sloganLanguage]});
      itemDetailEntriesFr.push({name: 'Langue du message', value: sloganLanguageMapFrench[orderItem.plateInfo.sloganLanguage]});
      itemDetailEntriesEn.push({name: 'Quantity', value: this.getPlateCount(orderItem.plateInfo)});
      itemDetailEntriesFr.push({name: 'Quantité', value: this.getPlateCount(orderItem.plateInfo)});
      productAdditionalProperties.push({name: 'plateType', value: orderItem.plateInfo.plateType});
      productAdditionalProperties.push({name: 'sloganLanguage', value: orderItem.plateInfo.sloganLanguage});
      productAdditionalProperties.push({name: 'graphicType', value: orderItem.plateInfo.graphicType});
      productLineItems.push({itemDetailEntriesEn, itemDetailEntriesFr, additionalProperties: productAdditionalProperties});

      products.push({productDetailEntriesEn, productDetailEntriesFr, items: productLineItems});
    });

    const subTotal: number = response.priceBreakdown.reduce((sum, item) => sum + (item.totalItemCost.unitCost || 0), 0);
    subTotalEntriesEn.push({name: 'Subtotal',  value: NumericValueUtils.toAmountInEnglish(subTotal)});
    subTotalEntriesFr.push({name: 'Total',  value: NumericValueUtils.toAmountInFrench(subTotal)});
    const totalTax: number = response.priceBreakdown.reduce((sum, item) => sum + (item.totalItemCost.taxAmount || 0), 0);
    subTotalEntriesEn.push({name: 'HST',  value: NumericValueUtils.toAmountInEnglish(totalTax)});
    subTotalEntriesFr.push({name: 'TVH',  value: NumericValueUtils.toAmountInFrench(totalTax)});

    totalEntriesEn.push({name: 'Total',  value: NumericValueUtils.toAmountInEnglish(response.totalAmount)});
    totalEntriesFr.push({name: 'Total',  value: NumericValueUtils.toAmountInFrench(response.totalAmount)});
    
    const totalOrderAmountField = getFieldByName('totalOrderAmount', field);
    totalOrderAmountField.Value = response.totalAmount;  // Saving the total order amount.

    return (
      {
        products,
        payment: { itemEntriesEn, itemEntriesFr, subTotalEntriesEn, subTotalEntriesFr, totalEntriesEn, totalEntriesFr}
    })
  }

  private handleCompletePayment(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    if ((response.statusCode == SERVICE_CALL_RESPONSE_CODES.COMPLETE_PAYMENT_SUCCESSFUL) || (response == SERVICE_CALL_RESPONSE_CODES.COMPLETE_PAYMENT_SUCCESSFUL)) {
      return response.data?.providerUrl;
    } else {
      return null;
    }
  }
}